"use client";

import {
  useState,
} from "react";

import {
  Tag,
  Store,
  LayoutGrid,
  Search,
  ChevronDown,
} from "lucide-react";

import {
  IProduct,
} from "@/types/product-d-t";

import GadgetProductCard from "../products/single-product/product-item";

import FloatingBasket from "../cart/FloatingBasket";

import styles from "./ShopArea.module.css";

type IProps = {
  allProducts: IProduct[];

  categoryName?: string;

  brandName?: string;

  searchText?: string;
};

const ShopArea = ({
  allProducts,
  categoryName,
  brandName,
  searchText,
}: IProps) => {
  // =====================================================
  // CONFIG
  // =====================================================

  const PRODUCTS_PER_LOAD = 80;

  // =====================================================
  // STATE
  // =====================================================

  const [
    visibleCount,
    setVisibleCount,
  ] = useState<number>(
    PRODUCTS_PER_LOAD
  );

  const [
    hoveredBtn,
    setHoveredBtn,
  ] = useState(false);

  // =====================================================
  // LOAD MORE
  // =====================================================

  const handleSeeMore = () => {
    setVisibleCount(
      (prev) =>
        Math.min(
          prev +
            PRODUCTS_PER_LOAD,
          allProducts.length
        )
    );
  };

  // =====================================================
  // PRODUCTS
  // =====================================================

  const productsToShow =
    allProducts.slice(
      0,
      visibleCount
    );

  // =====================================================
  // PAGE TYPE
  // =====================================================

  const isCategory =
    !!categoryName;

  const isBrand =
    !!brandName;

  const isSearch =
    !!searchText;

  // =====================================================
  // TITLE
  // =====================================================

  const title = isSearch
    ? `"${searchText}"`
    : categoryName ||
      brandName ||
      "All Products";

  // =====================================================
  // ICON
  // =====================================================

  const contextIcon =
    isSearch ? (
      <Search
        size={18}
        strokeWidth={2.5}
      />
    ) : isCategory ? (
      <Tag
        size={18}
        strokeWidth={2.5}
      />
    ) : isBrand ? (
      <Store
        size={18}
        strokeWidth={2.5}
      />
    ) : (
      <LayoutGrid
        size={18}
        strokeWidth={2.5}
      />
    );

  // =====================================================
  // LABEL
  // =====================================================

  const contextLabel =
    isSearch
      ? "Search Results"
      : isCategory
      ? "Category"
      : isBrand
      ? "Brand"
      : "Shop";

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <section
      className={
        styles.shopArea
      }
    >
      {/* =================================================
          PAGE HEADER
      ================================================= */}

      <div
        className={
          styles.simpleShopHeader
        }
      >
        <div
          className={
            styles.simpleShopInner
          }
        >
          <div
            className={
              styles.simpleContext
            }
          >
            {contextIcon}

            <span>
              {contextLabel}
            </span>
          </div>

          <h1
            className={
              styles.simpleTitle
            }
          >
            {title}
          </h1>

          <p
            className={
              styles.simpleSubtitle
            }
          >
            {allProducts.length}{" "}
            {allProducts.length === 1
              ? "product"
              : "products"}
          </p>
        </div>
      </div>

      {/* =================================================
          PRODUCTS SECTION
      ================================================= */}

      <div
        className={
          styles.productsSection
        }
      >
        <div
          className={
            styles.shopContainer
          }
        >
          {/* =================================================
              EMPTY
          ================================================= */}

          {allProducts.length ===
          0 ? (
            <div
              className={
                styles.emptyState
              }
            >
              <div
                className={
                  styles.emptyIcon
                }
              >
                🛍️
              </div>

              <h3
                className={
                  styles.emptyTitle
                }
              >
                No products found
              </h3>

              <p
                className={
                  styles.emptySub
                }
              >
                {isSearch
                  ? `No products matched "${searchText}". Try a different search.`
                  : isCategory
                  ? `No products available in the "${title}" category yet.`
                  : isBrand
                  ? `No products available from "${title}" yet.`
                  : "No products available at the moment."}
              </p>

              <a
                href="/shop"
                className={
                  styles.emptyCta
                }
              >
                Browse All Products
              </a>
            </div>
          ) : (
            <>
              {/* =================================================
                  PRODUCT GRID
              ================================================= */}

              <div
                className={
                  styles.productGrid
                }
              >
                {productsToShow.map(
                  (
                    product: any,
                    i: number
                  ) => (
                    <div
                      key={
                        product.id
                      }
                      className={
                        styles.productCell
                      }
                      style={{
                        animationDelay: `${
                          (i % 12) *
                          40
                        }ms`,
                      }}
                    >
                      <GadgetProductCard
                        product={
                          product
                        }
                      />
                    </div>
                  )
                )}
              </div>

              {/* =================================================
                  PROGRESS
              ================================================= */}

              {allProducts.length >
                PRODUCTS_PER_LOAD && (
                <div
                  className={
                    styles.progressSection
                  }
                >
                  <div
                    className={
                      styles.progressInfo
                    }
                  >
                    <span
                      className={
                        styles.progressText
                      }
                    >
                      Showing{" "}
                      <strong>
                        {Math.min(
                          visibleCount,
                          allProducts.length
                        )}
                      </strong>{" "}
                      of{" "}
                      <strong>
                        {
                          allProducts.length
                        }
                      </strong>{" "}
                      products
                    </span>

                    <span
                      className={
                        styles.progressPercent
                      }
                    >
                      {Math.round(
                        (Math.min(
                          visibleCount,
                          allProducts.length
                        ) /
                          allProducts.length) *
                          100
                      )}
                      %
                    </span>
                  </div>

                  <div
                    className={
                      styles.progressBarTrack
                    }
                  >
                    <div
                      className={
                        styles.progressBarFill
                      }
                      style={{
                        width: `${Math.round(
                          (Math.min(
                            visibleCount,
                            allProducts.length
                          ) /
                            allProducts.length) *
                            100
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* =================================================
                  LOAD MORE
              ================================================= */}

              {visibleCount <
                allProducts.length && (
                <div
                  className={
                    styles.seeMoreWrapper
                  }
                >
                  <button
                    type="button"
                    onClick={
                      handleSeeMore
                    }
                    className={
                      styles.seeMoreBtn
                    }
                    onMouseEnter={() =>
                      setHoveredBtn(
                        true
                      )
                    }
                    onMouseLeave={() =>
                      setHoveredBtn(
                        false
                      )
                    }
                  >
                    <span>
                      Load More Products
                    </span>

                    <ChevronDown
                      size={18}
                      style={{
                        transition:
                          "transform 0.3s ease",

                        transform:
                          hoveredBtn
                            ? "translateY(3px)"
                            : "translateY(0)",
                      }}
                    />
                  </button>

                  <p
                    className={
                      styles.seeMoreHint
                    }
                  >
                    {allProducts.length -
                      visibleCount}{" "}
                    more products
                    waiting
                  </p>
                </div>
              )}

              {/* =================================================
                  ALL LOADED
              ================================================= */}

              {visibleCount >=
                allProducts.length &&
                allProducts.length >
                  PRODUCTS_PER_LOAD && (
                  <div
                    className={
                      styles.allLoaded
                    }
                  >
                    <div
                      className={
                        styles.allLoadedLine
                      }
                    />

                    <span>
                      You've seen all{" "}
                      {
                        allProducts.length
                      }{" "}
                      products
                    </span>

                    <div
                      className={
                        styles.allLoadedLine
                      }
                    />
                  </div>
                )}
            </>
          )}
        </div>
      </div>

      {/* =================================================
          FLOATING BASKET

          IMPORTANT:
          এটাকে product loop-এর বাইরে রাখা হয়েছে।
          তাই পুরো Shop Area-তে একটাই basket থাকবে।
      ================================================= */}

      <FloatingBasket />
    </section>
  );
};

export default ShopArea;